import { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; 

function UserGroup() {
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [successMsg, setSuccessMsg] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("accessToken");
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // Fetch all roles
  const fetchGroups = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/staff/user-roles/`, {
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status} - ${res.statusText}`);
      }

      const data = await res.json();

      // Adjust if your API wraps the array (e.g. { data: [...], success: true })
      const rolesArray = Array.isArray(data) ? data : data?.data || data?.roles || [];

      setGroups(rolesArray);
    } catch (err) {
      console.error("Fetch roles failed:", err);
      setErrorMsg("Failed to load user groups. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const filtered = groups.filter((g) =>
    g.name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleAdd = () => {
    setEditId(null);
    setFormData({ name: "", description: "" });
    setOpen(true);
  };

  const handleEdit = (group) => {
    setEditId(group.id);
    setFormData({
      name: group.name || "",
      description: group.description || "",
    });
    setOpen(true);
  };

const handleSave = async () => {
  if (!formData.name.trim()) {
    setErrorMsg("Group name is required.");
    return;
  }

  setLoading(true);
  setErrorMsg("");
  setSuccessMsg("");

  try {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      throw new Error("No access token found. Please log in again.");
    }

    // FIXED: Use consistent URL format with trailing slash
    const baseUrl = `${API_BASE_URL}/api/staff/user-roles/`;
    let url = baseUrl;
    let method = "POST";

    const payload = { 
      name: formData.name.trim(), 
      description: formData.description.trim() 
    };

    if (editId) {
      // FIXED: Remove any potential double slashes
      url = `${baseUrl}${editId}/`;  // Add trailing slash here too if your API expects it
      method = "PUT"; // or "PATCH" - try PUT first
      
      // If PUT doesn't work, uncomment the next line:
      // method = "PATCH";
    }

    console.log(`→ Sending ${method} to ${url}`);
    console.log("Payload:", payload);

    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    console.log(`← Response status: ${res.status}`);

    if (!res.ok) {
      let errorDetail = "";
      try {
        const errData = await res.json();
        errorDetail = errData.message || errData.error || JSON.stringify(errData);
      } catch {
        errorDetail = await res.text();
      }
      throw new Error(`Server error ${res.status}: ${errorDetail || res.statusText}`);
    }

    const responseData = await res.json().catch(() => ({}));
    console.log("Success response:", responseData);

    setSuccessMsg(editId ? "Group updated successfully" : "Group added successfully");
    setOpen(false);
    await fetchGroups(); // refresh list
  } catch (err) {
    console.error("Save operation failed:", err);
    setErrorMsg(err.message || "Failed to save group. Check console for details.");
  } finally {
    setLoading(false);
  }
};

const handleDeleteClick = (id) => {
  setDeleteId(id);
  setDeleteDialogOpen(true);
};

const handleDeleteConfirm = async () => {
  if (!deleteId) return;

  setDeleting(true);
  setErrorMsg("");

  try {
    const res = await fetch(`${API_BASE_URL}/api/staff/user-roles/${deleteId}/`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || `HTTP ${res.status}`);
    }

    setSuccessMsg("Group deleted successfully");
    setDeleteDialogOpen(false);
    setDeleteId(null);
    await fetchGroups(); // refresh
  } catch (err) {
    console.error("Delete failed:", err);
    setErrorMsg(err.message || "Failed to delete group.");
  } finally {
    setDeleting(false);
  }
};

const handleDeleteCancel = () => {
  setDeleteDialogOpen(false);
  setDeleteId(null);
};

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} mb={1}>
        User Groups
      </Typography>
      <Typography color="text.secondary" mb={4}>
        Manage user roles and group definitions
      </Typography>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <TextField
            size="small"
            placeholder="Search group..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="contained" onClick={handleAdd} disabled={loading}>
            Add Group
          </Button>
        </Box>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMsg}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell>S.No</TableCell>
                  <TableCell>Group Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered.length > 0 ? (
                  filtered.map((g, index) => (
                    <TableRow key={g.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{g.name}</TableCell>
                      <TableCell>{g.description || "—"}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          sx={{ mr: 1 }}
                          variant="outlined"
                          onClick={() => handleEdit(g)}
                          disabled={loading}
                        >
                          Edit
                        </Button>
                      <Button
  size="small"
  color="error"
  variant="contained"
  onClick={() => handleDeleteClick(g.id)}
  disabled={loading || deleting}
>
  Delete
</Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary" py={4}>
                        {groups.length === 0 ? "No groups available" : "No matching groups"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Edit Group" : "Add Group"}</DialogTitle>

        <DialogContent>
          <TextField
            label="Group Name"
            fullWidth
            required
            margin="normal"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!formData.name.trim() && !!errorMsg}
            helperText={!formData.name.trim() && !!errorMsg ? "Name is required" : ""}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading} data-app-cancel="true">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !formData.name.trim()}
          >
            {loading ? <CircularProgress size={24} /> : editId ? "Update Group" : "Save Group"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
<Dialog 
  open={deleteDialogOpen} 
  onClose={handleDeleteCancel}
  maxWidth="xs" 
  fullWidth
>
  <DialogTitle>Confirm Delete</DialogTitle>
  <DialogContent>
    <Typography>
      Are you sure you want to delete this group? This action cannot be undone.
    </Typography>
  </DialogContent>
  <DialogActions>
    <Button 
      onClick={handleDeleteCancel} 
      disabled={deleting}
     data-app-cancel="true">
      Cancel
    </Button>
    <Button 
      onClick={handleDeleteConfirm} 
      color="error" 
      variant="contained"
      disabled={deleting}
    >
      {deleting ? <CircularProgress size={24} /> : "Delete"}
    </Button>
  </DialogActions>
</Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={Boolean(successMsg)}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg("")}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccessMsg("")}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UserGroup;
